import { createRouter, createWebHistory } from "vue-router";
import { usePostHog } from "@/composables/usePostHog";
import { useAnalytics } from "@/composables/useAnalytics";
import {
  Signin,
  Signup,
  SetPassword,
  Acknowledgements,
  MFA,
  Dashboard,
  InvestmentDetails,
  MaintenanceScreen,
  ForgotPassword,
  ChangePassword,
  PurchaseFlow,
  ChangeInvestment,
  InvestorProfile,
  InvestorDocuments,
  InvestmentSuccess,
  PersonalDetails,
  ContactUs,
  Portfolio,
  Newsfeed,
} from "@/pages";
import { useAuthStore } from "@/stores";
import { getProductParam } from "@/composables/useOfferingExperiment";

let routes = [];

if (import.meta.env.VITE_MAINTANENCE_MODE === "true") {
  routes = [
    {
      path: "/:pathMatch(.*)*",
      redirect: "/maintenance",
      meta: { requires_auth: false },
    },
    {
      path: "/maintenance",
      name: "MaintenanceScreen",
      component: MaintenanceScreen,
      meta: { requires_auth: false },
    },
  ];
} else {
  routes = [
    {
      path: "/:catchAll(.*)",
      redirect: "/dashboard",
    },
    {
      path: "/",
      redirect: "/dashboard",
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: Dashboard,
      meta: { requires_auth: true },
    },
    {
      path: "/newsfeed",
      name: "newsfeed",
      component: Newsfeed,
      meta: { requires_auth: true },
    },
    {
      path: "/portfolio",
      name: "portfolio",
      component: Portfolio,
      meta: { requires_auth: true },
    },
    {
      path: "/investment-details",
      name: "investment-details",
      component: InvestmentDetails,
      meta: { requires_auth: true },
    },
    {
      path: "/make-an-investment",
      name: "make-an-investment",
      component: PurchaseFlow,
      meta: { requires_auth: true },
    },
    {
      path: "/change-investment",
      name: "change-investment",
      component: ChangeInvestment,
      meta: { requires_auth: true },
    },
    {
      path: "/thank-you",
      name: "thank-you",
      component: InvestmentSuccess,
      meta: { requires_auth: true },
    },
    {
      path: "/profile",
      name: "profile",
      component: InvestorProfile,
      meta: { requires_auth: true },
    },
    {
      path: "/documents",
      name: "documents",
      component: InvestorDocuments,
      meta: { requires_auth: true },
    },
    {
      path: "/contact-us",
      name: "contact-us",
      component: ContactUs,
      meta: { requires_auth: true },
    },
    {
      path: "/sign-in",
      name: "signin",
      component: Signin,
      meta: { requires_auth: false },
    },
    {
      path: "/sign-up",
      name: "signup",
      component: Signup,
      meta: { requires_auth: false },
    },
    {
      path: "/sign-up/set-password",
      name: "set-password",
      component: SetPassword,
      meta: { requires_auth: false },
    },
    {
      path: "/sign-up/personal-details",
      name: "personal-details",
      component: PersonalDetails,
      meta: { requires_auth: false },
    },
    {
      path: "/sign-up/acknowledgements",
      name: "acknowledgements",
      component: Acknowledgements,
      meta: { requires_auth: false },
    },
    {
      path: "/sign-up/auth-verification",
      name: "signup-auth-verification",
      component: MFA,
      meta: { requires_auth: false },
    },
    {
      path: "/sign-in/auth-verification",
      name: "signin-auth-verification",
      component: MFA,
      meta: { requires_auth: false },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: ForgotPassword,
      meta: { requires_auth: false },
    },
    {
      path: "/change-password",
      name: "change-password",
      component: ChangePassword,
      meta: { requires_auth: false },
    },
  ];
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const { posthog } = usePostHog();
const { trackScreen } = useAnalytics();

const SCREEN_MAP = {
  "/dashboard": ["dashboard", "dashboard"],
  "/portfolio": ["portfolio", "portfolio"],
  "/investment-details": ["investment_details", "investment_details"],
  "/make-an-investment": ["purchase_flow", "purchase_flow"],
  "/thank-you": ["purchase_flow", "thank_you"],
  "/profile": ["profile", "profile"],
  "/documents": ["documents", "documents"],
  "/contact-us": ["contact_us", "contact_us"],
  "/newsfeed": ["newsfeed", "newsfeed"],
  "/sign-in": ["auth", "sign_in"],
  "/sign-up": ["auth", "sign_up"],
  "/sign-up/set-password": ["auth", "set_password"],
  "/sign-up/personal-details": ["auth", "personal_details"],
  "/sign-up/acknowledgements": ["auth", "acknowledgements"],
  "/sign-up/auth-verification": ["auth", "mfa"],
  "/sign-in/auth-verification": ["auth", "mfa"],
  "/forgot-password": ["auth", "forgot_password"],
  "/change-password": ["auth", "change_password"],
};

router.afterEach((to) => {
  const mapped = SCREEN_MAP[to.path];
  if (mapped) trackScreen(mapped[0], mapped[1]);
});

// Variable to store previous route
let previousRoute = null;

router.beforeEach((to, from, next) => {
  previousRoute = from;
  next();
});

router.beforeEach((to) => {
  const product = getProductParam();
  if (to.path === "/make-an-investment" && product && !to.query.product) {
    return { ...to, query: { ...to.query, product } };
  }
});

if (import.meta.env.VITE_MAINTENANCE !== "true") {
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    if (authStore.current_user) {
      // Handle authenticated users trying to manually go to guest routes
      if (to.matched.some((record) => record.meta.requires_auth === false)) {
        next("/dashboard");
      } else {
        next();
      }
    } else {
      // Handle guest routes
      if (to.matched.some((record) => record.meta.requires_auth === false)) {
        next();
      } else {
        // Default case
        next("/sign-in");
      }
    }
  });
}

export default router;

export { previousRoute };
